import { useState, useMemo } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { galleryImages } from "../mock";

const categories = ["Tutti", "Giardini", "Realizzazioni", "Manutenzione", "Potature", "Scavi", "Gru", "Irrigazione", "Famiglia"];

export default function Gallery() {
  const [filter, setFilter] = useState("Tutti");
  const [lightbox, setLightbox] = useState(null); // index

  const filtered = useMemo(
    () => (filter === "Tutti" ? galleryImages : galleryImages.filter((g) => g.category === filter)),
    [filter]
  );

  const openAt = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = (e) => {
    e.stopPropagation();
    setLightbox((i) => (i === 0 ? filtered.length - 1 : i - 1));
  };
  const next = (e) => {
    e.stopPropagation();
    setLightbox((i) => (i === filtered.length - 1 ? 0 : i + 1));
  };

  return (
    <div>
      {/* HERO */}
      <section className="pt-40 pb-16 bg-sage-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl reveal">
            <span className="text-xs tracking-[0.3em] uppercase text-sage-600">Portfolio</span>
            <h1 className="font-serif text-5xl md:text-7xl text-sage-900 mt-4 leading-[1.05] mb-6">
              Foto<span className="italic text-sage-700">gallery</span>
            </h1>
            <p className="text-lg text-sage-800/70 leading-relaxed">
              Uno sguardo su alcuni dei giardini che abbiamo creato e curato negli anni. Ogni immagine racconta una storia diversa.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-8 bg-white sticky top-20 z-30 border-b border-sage-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-3 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-6 py-2.5 rounded-full text-sm tracking-wide transition-all whitespace-nowrap ${
                filter === c
                  ? "bg-sage-700 text-white"
                  : "bg-sage-50 text-sage-800 hover:bg-sage-100"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((g, i) => (
              <button
                key={g.id}
                onClick={() => openAt(i)}
                className="group relative overflow-hidden rounded aspect-[4/3] reveal bg-sage-100"
              >
                {g.rotate ? (
                  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                    <img
                      src={g.url}
                      alt={g.title}
                      className="max-w-none transition-transform duration-700 group-hover:scale-110"
                      style={{ height: "260px", width: "auto", transform: "rotate(90deg)" }}
                    />
                  </div>
                ) : g.url.endsWith(".mp4") ? (
                  <video
                    src={`${g.url}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <img
                    src={g.url}
                    alt={g.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                )}
                <span className="absolute inset-x-0 bottom-0 bg-sage-900/75 px-4 py-3 text-left text-sm text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {g.title}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox !== null && filtered[lightbox] && (
        <div
          onClick={close}
          className="fixed inset-0 z-[100] bg-sage-900/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
        >
          <button onClick={close} className="absolute top-6 right-6 text-white hover:text-sage-200" aria-label="Chiudi">
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <button onClick={prev} className="absolute left-6 text-white hover:text-sage-200 p-3" aria-label="Precedente">
            <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full flex flex-col items-center">
            {filtered[lightbox].rotate ? (
              <img
                src={filtered[lightbox].url}
                alt={filtered[lightbox].title}
                className="max-w-none rounded"
                style={{ height: "min(70vw, 80vh)", width: "auto", transform: "rotate(90deg)", marginTop: "5vh", marginBottom: "5vh" }}
              />
            ) : filtered[lightbox].url.endsWith(".mp4") ? (
              <video
                src={filtered[lightbox].url}
                controls
                autoPlay
                playsInline
                className="w-full max-h-[80vh] object-contain rounded"
              />
            ) : (
              <img src={filtered[lightbox].url} alt={filtered[lightbox].title} className="w-full max-h-[80vh] object-contain rounded" />
            )}
            <p className="mt-4 text-center text-lg text-white">{filtered[lightbox].title}</p>
          </div>
          <button onClick={next} className="absolute right-6 text-white hover:text-sage-200 p-3" aria-label="Successiva">
            <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}
